import { BrowserAuthError, InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalInstance, loginRequest } from '../auth/msalConfig';

const BASE_URL = import.meta.env.VITE_API_BASE_URL as string;

/**
 * Structured error class for non-ok API responses.
 * Callers can inspect status and body for targeted error handling.
 * `message` carries the server's RFC 7807 detail when present.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, body: unknown) {
    const problem = (typeof body === 'object' && body !== null ? body : {}) as {
      title?: unknown;
      detail?: unknown;
    };
    const detail = typeof problem.detail === 'string' ? problem.detail : undefined;
    const title = typeof problem.title === 'string' ? problem.title : undefined;
    super(`${status.toString()}${title ? ` ${title}` : ''}${detail ? `: ${detail}` : ''}`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/**
 * Sign-in / token acquisition failure (MSAL or Entra). Carries the MSAL error code and
 * the Entra description, which contains the AADSTS code (e.g. "AADSTS650053: …").
 */
export class AuthTokenError extends Error {
  readonly errorCode: string;

  constructor(errorCode: string, description: string) {
    super(description);
    this.name = 'AuthTokenError';
    this.errorCode = errorCode;
  }
}

/** Duck-typed MSAL AuthError (keeps this module testable without MSAL internals). */
function asMsalError(error: unknown): { errorCode: string; errorMessage: string } | null {
  if (typeof error !== 'object' || error === null) return null;
  const candidate = error as { errorCode?: unknown; errorMessage?: unknown; message?: unknown };
  if (typeof candidate.errorCode !== 'string') return null;
  const message =
    typeof candidate.errorMessage === 'string' && candidate.errorMessage
      ? candidate.errorMessage
      : typeof candidate.message === 'string'
        ? candidate.message
        : '';
  return { errorCode: candidate.errorCode, errorMessage: message };
}

/** Human-readable message for any error thrown by apiFetch (status + server detail). */
export function describeApiError(error: unknown): string {
  if (error instanceof ApiError) return `HTTP ${error.message}`;
  if (error instanceof AuthTokenError) {
    return `Sign-in error (${error.errorCode}): ${error.message}`;
  }
  const msal = asMsalError(error);
  if (msal) return `Sign-in error (${msal.errorCode}): ${msal.errorMessage}`;
  if (error instanceof DOMException && error.name === 'TimeoutError') {
    return 'The request timed out. The server may still be processing it — refresh to check.';
  }
  if (error instanceof Error) return error.message;
  return String(error);
}

/** Upper bound for token acquisition during a mutation — never leave "Applying…" hanging. */
const MUTATION_TOKEN_TIMEOUT_MS = 30_000;

/**
 * Acquires the API access token (scope VITE_API_SCOPE, i.e. audit.read) silently.
 * The same token is used for reads AND writes — write access is authorized by the
 * Audit.Admin app role in its `roles` claim, never by an extra scope.
 *
 * Reads: when interaction is required (expired session, MFA) it redirects to sign-in;
 * the returned Promise never resolves because the browser navigates away.
 *
 * Mutations (`interactive: false`): never redirects or opens a popup. Any failure —
 * including a timeout — rejects with AuthTokenError so the caller can end its pending
 * state and show the AADSTS code and description.
 */
async function getAccessToken(options?: { interactive?: boolean }): Promise<string> {
  const interactive = options?.interactive ?? true;
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];

  if (!account) {
    throw new AuthTokenError('no_account', 'You are not signed in. Reload the page to sign in.');
  }

  if (!interactive) {
    try {
      const result = await Promise.race([
        msalInstance.acquireTokenSilent({ ...loginRequest, account }),
        new Promise<never>((_, reject) =>
          setTimeout(
            () =>
              reject(
                new AuthTokenError(
                  'token_timeout',
                  'Could not get a sign-in token within 30 seconds. Reload the page and try again.'
                )
              ),
            MUTATION_TOKEN_TIMEOUT_MS
          )
        ),
      ]);
      return result.accessToken;
    } catch (error) {
      if (error instanceof AuthTokenError) throw error;
      const msal = asMsalError(error);
      const needsSignIn = error instanceof InteractionRequiredAuthError;
      throw new AuthTokenError(
        msal?.errorCode ?? 'token_error',
        (msal?.errorMessage || (error instanceof Error ? error.message : String(error))) +
          (needsSignIn ? ' Reload the page to sign in again, then retry.' : '')
      );
    }
  }

  try {
    const result = await msalInstance.acquireTokenSilent({ ...loginRequest, account });
    return result.accessToken;
  } catch (error) {
    // Silent token refresh failed — redirect to interactive login for recoverable errors.
    // InteractionRequiredAuthError: token expired, MFA needed, consent required.
    // BrowserAuthError timed_out: hidden iframe blocked (e.g. X-Frame-Options) or slow network.
    const isInteractionRequired = error instanceof InteractionRequiredAuthError;
    const isTimedOut = error instanceof BrowserAuthError && error.errorCode === 'timed_out';

    if (isInteractionRequired || isTimedOut) {
      console.warn(
        '[getAccessToken] silent refresh failed, redirecting to login',
        error instanceof Error ? error.message : error
      );
      void msalInstance.loginRedirect(loginRequest);
      return new Promise<string>(() => {});
    }
    throw error;
  }
}

/** Decode a JWT payload (no signature check — display/gating only; the API enforces). */
export function decodeJwtPayload(token: string): Record<string, unknown> {
  const segment = token.split('.')[1] ?? '';
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const bytes = Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes)) as Record<string, unknown>;
}

/** App roles from the current API access token (e.g. ["Audit.Admin"]). */
export async function getAccessTokenRoles(): Promise<string[]> {
  const claims = decodeJwtPayload(await getAccessToken());
  return Array.isArray(claims.roles)
    ? claims.roles.filter((r): r is string => typeof r === 'string')
    : [];
}

/**
 * Token-injecting fetch wrapper for all API calls.
 *
 * Usage:
 *   const data = await apiFetch<ApiResponse<DeviceRecord>>('/api/audit/latest');
 *
 * `mutation: true` (PUT/POST that change data): token problems reject instead of
 * redirecting, so a pending Apply/Run always ends with a visible error.
 *
 * Throws ApiError on non-ok responses so callers and TanStack Query can handle
 * HTTP errors distinctly from network errors.
 */
export async function apiFetch<T>(
  path: string,
  options?: { method?: string; body?: unknown; timeoutMs?: number; mutation?: boolean }
): Promise<T> {
  const token = await getAccessToken({ interactive: !options?.mutation });

  const response = await fetch(`${BASE_URL}${path}`, {
    method: options?.method ?? 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    ...(options?.body !== undefined && { body: JSON.stringify(options.body) }),
    ...(options?.timeoutMs && { signal: AbortSignal.timeout(options.timeoutMs) }),
  });

  const text = await response.text();
  let body: unknown;
  try {
    body = JSON.parse(text) as unknown;
  } catch {
    body = text;
  }

  if (!response.ok) {
    throw new ApiError(response.status, body);
  }

  return body as T;
}
